import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getSpaceById, updateSpace } from '../api/spaceApi';

const SpaceContext = createContext({
  space: null,
  spaceLoading: true,
  updateSpaceCurrency: async () => {},
  refetchSpace: async () => {},
});

export function SpaceProvider({ children }) {
  const { activeSpace, isAuthenticated, signInError } = useAuth();
  const spaceId = (activeSpace?._isAdmin || activeSpace?._isFinance) ? null : (activeSpace?.spaceId ?? null);

  const [space, setSpace] = useState(null);
  const [spaceLoading, setSpaceLoading] = useState(true);

  // isAuthenticated guards against a stale spaceId in localStorage outliving a dead
  // session. signInError covers a stale MSAL account keeping isAuthenticated true after exchange fails.
  // Exposed as refetchSpace so pages that edit this same space elsewhere (e.g. Admin Portal's
  // Currency tab) can force this single, app-wide provider to pick up the change without a
  // full page reload — SpaceProvider mounts once above the router, so SPA navigation back to
  // Payments/Items otherwise keeps serving whatever was fetched at the last space switch.
  const refetchSpace = useCallback(() => {
    if (!isAuthenticated || signInError || !spaceId) {
      setSpace(null);
      setSpaceLoading(false);
      return Promise.resolve();
    }

    setSpace(null);
    setSpaceLoading(true);
    return getSpaceById()
      .then((res) => setSpace(res.data))
      .catch(() => {})
      .finally(() => setSpaceLoading(false));
  }, [spaceId, isAuthenticated, signInError]);

  useEffect(() => {
    refetchSpace();
  }, [refetchSpace]); // re-runs automatically whenever the active space changes

  const updateSpaceCurrency = useCallback(
    async (currencyId, modifiedBy) => {
      if (!space) return;
      const body = {
        platformTypeId: space.platformTypeId,
        entityId: space.entityId,
        sku: space.sku,
        link: space.link,
        isPublished: space.isPublished,
        isLive: space.isLive,
        currencyId,
        modifiedBy: modifiedBy ?? space.modifiedBy,
        localizedDescription: space.localizedDescription,
      };
      const res = await updateSpace(body);
      setSpace(res.data);
    },
    [space],
  );

  return (
    <SpaceContext.Provider value={{ space, spaceLoading, updateSpaceCurrency, refetchSpace }}>
      {children}
    </SpaceContext.Provider>
  );
}

export const useSpace = () => useContext(SpaceContext);
